import { ComponentProps } from 'lib/component-props';
import { useI18n } from 'next-localization';
import BaseSkeleton from 'components/Generic/BaseSkeleton';
import BackToTopButton from 'components/Generic/Buttons/BackToTopButton';
import useHelpers from 'hooks/helpers';
import { useCallback, useEffect, useMemo } from 'react';
import { useLazyFetchDocumentsQuery } from '@/store/literatureHub/services/literatureHubAPI';
import Pagination from 'components/includes/Pagination';
import LiteratureHubTable from 'components/Pages/LiteratureHub/LiteratureHubTable';
import LiteratureFiltersSection from 'components/Pages/LiteratureHub/LiteratureFiltersSection';
import { useDispatch, useSelector } from 'react-redux';
import { fundCentreActions, getFundCentreData } from '@/store/fundCentre';
import { useRouter } from 'next/router';
import { AppliedFilters } from 'model/LiteratureHub';
import { useSitecoreContext } from 'hooks/useSitecoreContext';

type Options = {
  id: string;
  name: string;
};

type LiteratureHubProps = ComponentProps & {
  fields: {
    filterMappings: {
      value: {
        displayName: string;
        id: string;
        options: Array<Options>;
      }[];
    };
  };
};

const ITEMS_PER_PAGE = 20;

const LiteratureHub = ({ fields }: LiteratureHubProps): JSX.Element => {
  const { t } = useI18n();
  const { localizeDate, updateUrlParams } = useHelpers();
  const router = useRouter();
  const { sitecoreContext } = useSitecoreContext();
  const dispatch = useDispatch();
  const { isReady, query } = router;
  const { page } = router.query;
  const [fetchDocuments, { data, isFetching }] = useLazyFetchDocumentsQuery();
  const { searchQuery, currentPage, paginationData } = useSelector(getFundCentreData);

  const {
    site: { name },
  } = sitecoreContext;

  const appliedFilters: Array<AppliedFilters> = useSelector(
    (state) => state.fundCentre.appliedFilters,
    (a, b) => JSON.stringify(a) === JSON.stringify(b)
  );

  const totalResults: number = useMemo(() => {
    return data?.resultCount ?? 0;
  }, [data]);

  const maxPages: number = Math.ceil(totalResults / paginationData?.take) || 1;

  const updatePageNumber = useCallback(
    (currentPage: number) => {
      dispatch(fundCentreActions.setCurrentPage(currentPage));
    },
    [dispatch]
  );

  const resultFrom: number = useMemo(() => {
    return totalResults > 0 ? paginationData?.take * (currentPage - 1) + 1 : 0;
  }, [totalResults, currentPage, paginationData]);

  const resultTo: number = useMemo(() => {
    return paginationData?.take * (currentPage - 1) + data?.value.length;
  }, [paginationData, currentPage, data]);

  const filters = useMemo(() => {
    return fields.filterMappings?.value.map((filter) => ({
      ...filter,
      displayName: t(filter.displayName),
      options: filter.options.map((option) => {
        return {
          ...option,
          text: t(option.name),
          showAggregateCount: false,
          selected: query[filter.id]?.includes(option.id) || false,
          applied: query[filter.id]?.includes(option.id) || false,
        };
      }),
    }));
  }, [query, fields.filterMappings, t]);

  const payload = useMemo(() => {
    return {
      skip: paginationData.skip,
      take: ITEMS_PER_PAGE,
      searchQuery: searchQuery.name,
      filters: appliedFilters,
      site: name,
    };
  }, [appliedFilters, paginationData, searchQuery, name]);

  const fetchData = async () => {
    try {
      await fetchDocuments(payload);
    } catch (e) {
      console.error(e);
    }
  };

  const onPageSelect = (currentPage: number) => {
    updatePageNumber(currentPage);
    updateUrlParams([
      {
        name: 'page',
        value: currentPage,
      },
    ]);
    updatePaginationData(currentPage);
  };

  const updatePaginationData = (currentPage: number) => {
    dispatch(
      fundCentreActions.setPaginationData({
        skip: (currentPage - 1) * ITEMS_PER_PAGE,
        take: ITEMS_PER_PAGE,
      })
    );
  };

  useEffect(() => {
    if (page) {
      if (!isNaN(+page) && +page > 0) {
        dispatch(
          fundCentreActions.setPaginationData({
            ...paginationData,
            skip: (+page - 1) * paginationData.take,
          })
        );
        updatePageNumber(+page);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, payload]);

  return (
    <section className="default-section-padding background-white">
      <div className="container abrdn-table">
        {/*isReady used to properly set preselected filters from URL if there's any*/}
        {isReady && (
          <LiteratureFiltersSection
            filters={filters}
            appliedFilters={appliedFilters}
            itemsPerPage={ITEMS_PER_PAGE}
            className="mb-4 mb-lg-5"
          />
        )}
        {!isFetching && (
          <div className="mb-4">
            <h3>{t('literatureHubResultsLabel')}</h3>
          </div>
        )}
        <LiteratureHubTable
          showSkeleton={isFetching}
          paginationData={paginationData}
          documents={data?.value}
          localizeDate={localizeDate}
        />
        <div className="d-flex flex-column-reverse flex-xl-row justify-content-between align-items-center">
          {!isFetching ? (
            <>
              <p className="footnote text-grey">
                {t('paginationDisplayingResults')
                  .replace('{token.range}', `${resultFrom} - ${resultTo}`)
                  .replace('{token.total}', `${totalResults}`)}
              </p>
              <BackToTopButton className="mx-0 my-3 my-lg-4" />
            </>
          ) : (
            <>
              <BaseSkeleton width="320px" height="32px" />
              <BaseSkeleton width="320px" height="32px" className="mx-0 my-3 my-lg-4" />
            </>
          )}
        </div>
        <Pagination
          currentPage={currentPage}
          maxPages={maxPages}
          className="justify-content-center mt-4 mt-md-8"
          isLoading={isFetching}
          showSkeleton={isFetching}
          onSelect={onPageSelect}
        />
      </div>
    </section>
  );
};

export default LiteratureHub;
